<template>
  <BillingFeatureGate feature="technician_earnings"
                      variant="page"
                      :title="$t('billing.features.technician_earnings.pageTitle')"
                      :description="$t('billing.features.technician_earnings.tooltip')">
    <div>
      <Toolbar class="p-mb-4 p-mt-4">
        <template #start>
          <div style="font-size: 1.4em; color: inherit" class="p-mr-3">{{ $t('Earnings') }}</div>
          <AddNewButton v-if="$store.getters.isAccountantOrHigher"
                        :disabled="employeeIsLoading"
                        :loading="employeeIsLoading"
                        :label="$t('Add')"
                        @click="addNewItem"/>
        </template>
        <template #end>
          <DatesRangeCalendar :disabled="dataTableIsLoading" @change-dates-range="changeDatesRange" v-show="true"/>
        </template>
      </Toolbar>
      <div class="p-d-flex p-ai-center p-flex-wrap p-mb-4 p-mt-0">
        <div class="result-block p-m-1">
          {{ $t('Total sum') }}: <b>{{ formatCurrency(totalPaymentsSum) }}</b>
        </div>
        <template v-if="credit || debit">
          <div class="result-block p-m-1">
            {{ $t('Credit') }}: <span class="green-color">{{ formatCurrency(credit) }}</span>
          </div>
          <div class="result-block p-m-1">
            {{ $t('Debit') }}: <span class="warning-color">{{ formatCurrency(debit) }}</span>
          </div>
        </template>
      </div>
      <Spinner v-if="user && isLoading"/>
      <DesktopDataTable v-else
                        :dataTableIsLoading="dataTableIsLoading"
                        :itemsArrayIsEmpty="itemsArrayIsEmpty"
                        :sort="sort"
                        :showSearchInput="false"
                        :showButtons="actionButtonsCount > 0"
                        :actionButtonsCount="actionButtonsCount"
                        @sort-items="sortItems"
                        :headers="[
              {name: 'order', title: $t('Order'), sortable: false},
              {name: 'car', title: $t('Car'), sortable: false},
              {name: 'name', title: $t('Name'), sortable: false},
              {
                name: 'qty',
                title: $t('employeePaymentCalculatedQtyLabel'),
                hint: $t('employeePaymentCalculatedQtyTooltip'),
                sortable: false
              },
              // {name: 'unit_price', title: $t('unitPriceLabel'), sortable: false},
              // {name: 'service_total_amount', title: $t('employeePaymentCalculatedAmountLabel'), sortable: false},
              // {name: 'share_percent', title: $t('shareLabel'), sortable: false},
              // {name: 'technician_amount', title: $t('employeePaymentBaseLabel'), sortable: false},
              // {name: 'time', title: $t('technicianTimeLabel'), sortable: false},
              {
                name: 'service_calculation',
                title: $t('employeePaymentServiceCalculationLabel'),
                sortable: false
              },
              {
                name: 'share_percent',
                title: $t('shareLabel'),
                minWidth: '60px',
                sortable: false
              },
              {
                name: 'technician_calculation',
                title: $t('employeePaymentTechnicianCalculationLabel'),
                sortable: false
              },
              {name: 'percent', title: $t('Percent'), sortable: false},
              {name: 'sum', title: $t('employeePaymentAmountLabel'), sortable: false},
              {name: 'date', title: $t('Date'), sortable: false},
              {name: 'created_at', title: $t('Created'), sortable: true},
          ]">
        <template v-slot:body>
          <tr v-for="item of items" :key="item.id"
              :class="{'updated-item': isItemUpdated(item.id)}">
            <td class="table-data">
          <span v-if="item.order?.uid">
            <router-link :to="{ path: `/orders/${item.order.uid}` }"
                         :target="settings.openAllLinksInSameTab ? '_self' : '_blank'"
                         rel="noopener"
                         draggable="false">
              {{ item.order.number }}
            </router-link>
          </span>
              <i v-else class="ti-minus"/>
            </td>
            <td class="table-data">
          <span v-if="item.car?.uid && formatCarLabel(item.car)">
            <router-link :to="{ path: `/vehicles/${item.car.uid}` }"
                         class="p-d-flex p-flex-wrap"
                         :target="settings.openAllLinksInSameTab ? '_self' : '_blank'"
                         rel="noopener"
                         draggable="false">
              <div>{{ formatCarLabel(item.car) }}</div>
            </router-link>
          </span>
              <i v-else class="ti-minus"/>
            </td>
            <td class="table-data">{{ item.description }}</td>

            <!--        <td class="table-data">-->
            <!--          <span v-if="item.qty !== null && item.qty !== undefined">-->
            <!--            {{ formatDecimal(item.qty) }} / 6,00-->
            <!--          </span>-->
            <!--          <i v-else class="ti-minus"/>-->
            <!--        </td>-->

            <td class="table-data number-cell">
              <template v-if="item.qty !== null && item.qty !== undefined">
            <span :class="{'warning-color': isCalculatedQtyExceeded(item.qty, item.orderPosition?.qty)}">
              {{ formatDecimal(item.qty) }}
            </span>
                <template v-if="item.orderPosition?.qty !== null && item.orderPosition?.qty !== undefined">
                  / {{ formatDecimal(item.orderPosition.qty) }}
                </template>
              </template>
              <i v-else class="ti-minus"/>
            </td>

            <!--        <td class="table-data">-->
            <!--          <span v-if="getPaymentUnitPrice(item) !== null">-->
            <!--            {{ formatCurrency(getPaymentUnitPrice(item)) }}-->
            <!--          </span>-->
            <!--          <i v-else class="ti-minus"/>-->
            <!--        </td>-->

            <td class="table-data">
              <div>
                <span v-if="item.service_total_amount !== null && item.service_total_amount !== undefined">
                  {{ formatCurrency(item.service_total_amount) }}
                </span>
                <i v-else class="ti-minus"/>
              </div>

              <div v-if="hasTime(item.total_time)"
                   class="payment-calculation-cell__secondary">
                <i class="ti-time"/>
                {{ formatHoursAndMinutes(item.total_time) }}
              </div>
            </td>

            <td class="table-data">
              <span v-if="item.share_percent !== null && item.share_percent !== undefined">
                {{ formatDecimal(item.share_percent, false) }}%
              </span>
              <i v-else class="ti-minus"/>
            </td>

            <!--        <td class="table-data">-->
            <!--          <span v-if="getPaymentTechnicianAmount(item) !== null">-->
            <!--            {{ formatCurrency(getPaymentTechnicianAmount(item)) }}-->
            <!--          </span>-->
            <!--          <i v-else class="ti-minus"/>-->
            <!--        </td>-->

            <!--        <td class="table-data number-cell">-->
            <!--          <span v-if="hasTime(item.time)">-->
            <!--            {{ formatHoursAndMinutes(item.time) }}-->
            <!--          </span>-->
            <!--          <i v-else class="ti-minus"/>-->
            <!--        </td>-->

            <td class="table-data">
              <span v-if="isFullShare(item.share_percent)"
                    class="payment-calculation-cell__same">
                {{ $t('sameAsServiceCalculationLabel') }}
              </span>

              <template v-else>
                <div>
                  <span v-if="getPaymentTechnicianAmount(item) !== null">
                    {{ formatCurrency(getPaymentTechnicianAmount(item)) }}
                  </span>
                  <i v-else class="ti-minus"/>
                </div>

                <div v-if="hasTime(item.time)"
                     class="payment-calculation-cell__secondary">
                  <i class="ti-time"/>
                  {{ formatHoursAndMinutes(item.time) }}
                </div>
              </template>
            </td>

            <td class="table-data">
              <span v-if="item.percent !== null && item.percent !== undefined && item.order_position_id">
                {{ formatDecimal(item.percent) }}%
              </span>
              <i v-else class="ti-minus"/>
            </td>

            <td class="table-data">
              <span :class="item.sum >= 0 ? 'green-color' : 'warning-color'"><span v-if="item.sum >= 0">+</span>{{ formatCurrency(item.sum) }}</span>
            </td>

            <td class="table-date-line">
              <span v-if="item.date">{{ formatDate(item.date) }}</span>
              <i v-else class="ti-minus"/>
            </td>

            <td class="table-date-line">
              <span v-if="item.created_at">
                {{ formatBy(item.created_at, item.creator) }}
              </span>
              <i v-else class="ti-minus"/>
            </td>

            <td v-show="actionButtonsCount">
              <div class="actions">
                <EditButton :disabled="employeeIsLoading"
                            @click="editItem(item)" />
                <DeleteButton @click="confirmItemDelete(item)"
                              :disabled="employeeIsLoading"/>
              </div>
            </td>
          </tr>
        </template>
      </DesktopDataTable>

      <OwnPaginator v-show="!itemsArrayIsEmpty"
                    :rows="pagination.perPage"
                    :currentPage="pagination.currentPage - 1"
                    :totalPages="pagination.pageCount"
                    :totalRecords="pagination.totalCount"
                    :rowsPerPageOptions="[5,10,25,50,100,200]"
                    :pageLinkSize="11"/>

      <TotalRecordsData :pagination="pagination" :itemsArrayIsEmpty="itemsArrayIsEmpty"/>

      <EmployeePaymentModal :visible="itemModal"
                            :item="item"
                            :userId="user?.id"
                            @update-items="updateItems"
                            @close="closeItemModal"/>

      <ConfirmDeleteModal :visible="confirmItemDeleteModal"
                          :modalDataIsLoading="modalDataIsLoading"
                          @close="closeConfirmItemDeleteModal"
                          @delete-item="deleteItem"/>
    </div>
  </BillingFeatureGate>
</template>

<script>
import httpClient from '@/services/http.services'
import datatableMixins from "@/mixins/datatableMixins";
import formatMixins from "@/mixins/formatMixins";
import EmployeePaymentModal from "@/pages/employees/components/EmployeePaymentModal";
import DatesRangeCalendar from "@/components/DatesRangeCalendar";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import billingFeaturesMixins from '@/mixins/billingFeaturesMixins'
import BillingFeatureGate from '@/components/billing/BillingFeatureGate.vue'

export default {
  mixins: [ showErrorsMixins, datatableMixins, formatMixins, billingFeaturesMixins ],
  components: { EmployeePaymentModal, DatesRangeCalendar, BillingFeatureGate },
  emits: ['update-items', 'updateItems'],
  props: {
    user: {
      type: Object,
      default: null,
    },
    employeeIsLoading: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      totalPaymentsSum: 0,
      debit: 0,
      credit: 0,
      confirmModal: false,
      fieldsExpand: 'car,order,creator,orderPosition',
      apiLink: 'employee-payment',
      deleteItemUrl: 'employee-payment/delete',
      sort: {
        column: 'date',
        direction: '-',
      },
    }
  },
  watch: {
    billingEntitlementsReady() {
      this.initializeFeaturePage()
    },
    '$route'() {
      const route = this.$route.path.split('/')
      if (this.$route.path === '/login' || route.indexOf('employees') === -1 || route.indexOf('earnings') === -1) {
        return false
      }
      this.checkRouteQuery()
      this.getItems()
    },
  },
  mounted() {
    this.initializeFeaturePage()
  },
  methods: {
    initializeFeaturePage() {
      if (!this.billingEntitlementsReady || !this.canUseTechnicianEarnings) return
      if (!this.$route.params.uid) return
      this.checkRouteQuery()
      this.generateThisMonthRange()
      this.$store.commit('SET_FETCH_IN_FLIGHT', false)
      this.getPageData()
    },
    getPageData() {
      if (!this.canUseTechnicianEarnings) return
      this.getItems()
    },
    isFullShare(value) {
      const share = Number(value)

      return Number.isFinite(share) && share === 100
    },
    isCalculatedQtyExceeded(calculatedQty, positionQty) {
      const calculated = Number(calculatedQty)
      const position = Number(positionQty)

      if (
          !Number.isFinite(calculated) ||
          !Number.isFinite(position)
      ) {
        return false
      }

      return Math.abs(calculated) > Math.abs(position)
    },
    getPaymentUnitPrice(item) {
      const qty = Number(item.qty || 0)

      if (
          item.service_total_amount !== null &&
          item.service_total_amount !== undefined &&
          qty !== 0
      ) {
        return Number(item.service_total_amount) / qty
      }

      const serviceSellPrice = Number(item.service_sell_price || 0)
      const sharePercent = Number(item.share_percent || 0)

      if (serviceSellPrice && sharePercent) {
        return serviceSellPrice / (sharePercent / 100)
      }

      if (item.service_sell_price !== null && item.service_sell_price !== undefined) {
        return serviceSellPrice
      }

      return null
    },

    getPaymentTechnicianAmount(item) {
      const serviceTotalAmount = Number(item.service_total_amount || 0)
      const sharePercent = Number(item.share_percent || 0)

      if (
          item.service_total_amount !== null &&
          item.service_total_amount !== undefined &&
          item.share_percent !== null &&
          item.share_percent !== undefined
      ) {
        return +(serviceTotalAmount * (sharePercent / 100)).toFixed(2)
      }

      const qty = Number(item.qty || 0)
      const serviceSellPrice = Number(item.service_sell_price || 0)

      if (
          item.service_sell_price !== null &&
          item.service_sell_price !== undefined &&
          qty !== 0
      ) {
        return +(serviceSellPrice * qty).toFixed(2)
      }

      return null
    },
    changeDatesRange({ fromTimestamp, toTimestamp }, loadData = false) {
      this.fromTimestamp = fromTimestamp
      this.toTimestamp = toTimestamp
      this.resetCurrentPageData()
      if (loadData) {
        this.getPageData()
      }
    },
    async getItems(showSpinner = false) {
      if (!this.canUseTechnicianEarnings) return
      if (!this.userCanGetData || this.fetchInFlight) return
      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      this.dataTableIsLoading = true
      this.disableEditButton = true
      if (showSpinner) {
        this.isLoading = true
      }
      const ctrl = this.setController?.('employeePayments')
      try {
        const { status, data, headers } = await httpClient({
          url: this.apiLink,
          method: 'post',
          params: {
            page: this.pagination.currentPage,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            search: this.processedSearchValue ?? null,
            expand: this.fieldsExpand,
          },
          data: {
            userId: this.user?.id,
            dateFrom: this.fromTimestamp,
            dateTo: this.toTimestamp,
          },
          signal: ctrl?.signal,
        })

        if (status === 200 && data) {
          const items = data.payments_list && Array.isArray(data.payments_list) ? data.payments_list : []
          const hasPaginationHeaders = !!headers['x-pagination-total-count']

          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount  = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = items.length
            this.pagination.pageCount  = 1
          }

          this.items = items

          this.totalPaymentsSum = data.total_sum ?? 0
          this.debit = data.debit ?? 0
          this.credit = data.credit ?? 0

          if (!items.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }
        } else {
          this.items = []
          this.showUnknownError()
        }
      } catch(err) {
        this.items = []
        this.totalPaymentsSum = 0
        this.debit = 0
        this.credit = 0
        this.showError(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.isLoading = false
        this.dataTableIsLoading = false
        this.disableEditButton = false
        if (ctrl && this._abortors?.employeePayments === ctrl) {
          this._abortors.employeePayments = null
        }
      }
    },
  },
  computed: {
    actionButtonsCount() {
      if (this.$store.getters.isAccountantOrHigher) {
        return 2
      } else {
        return 0
      }
    },
  }
}
</script>
<style scoped lang="scss">
.total-sum-block {
  font-size: 1.4em;
  //font-weight: 400;
}

.table-data {
  height: 45px;
}
.result-block {
  padding-right: 16px;
}

.payment-calculation-cell__secondary {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 3px;
  color: var(--text-color-secondary);
  font-size: 0.9em;
  white-space: nowrap;
}

.payment-calculation-cell__same {
  color: var(--text-color-secondary);
  font-size: 0.9em;
  white-space: nowrap;
}
</style>